import ChevronRight from '@mui/icons-material/ChevronRight';
import { Menu } from '@mui/material';
import styled, { css } from 'styled-components';
import { isEmbedded, isSingleModeUi } from '@cyberint/common-utils';
import { MONITOR_SCREENS } from '@cyberint/dsc';

const contentPadding = '10px';
const breakPoint = isEmbedded ? '(min-width: 0)' : MONITOR_SCREENS.monitor.gte_medium;
export const BreadCrumbWrapper = styled.div`
  display: flex;
  padding-left: ${!isSingleModeUi() && '80px'};
  align-items: flex-start;
  background-color: var(--app-bg);
  color: var(--breadcrumb-color);
  min-height: ${!isSingleModeUi() && 'var(--bread-crumb-wrapper)'};
  gap: var(--medium-gap);
  @media ${breakPoint} {
    padding-left: var(--content-margin-small);
  }
`;

export const CrumbContent = styled.div`
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  a {
    text-decoration: none;
    color: inherit;
  }
`;

export const CrumbTooltipContent = styled.div`
  a {
    text-decoration: unset;
    cursor: unset;
    color: unset;
  }
`;

export const CrumbWrapper = styled.div`
  position: relative;
  .tenants-loading {
    position: absolute;
    right: -5px;
    top: -5px;
  }

  display: flex;
  padding: 2px 0 0;
  align-items: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  /* The bordered crumb is the selected customer and reads as a select control, so its border and
     radius come from the select variables rather than from literals. Defaults keep the previous
     look: no visible border and the original 15px pill. */
  ${({ $border }) =>
    $border &&
    css`
      padding: 5px 10px;
      font-size: 11px;
      font-weight: 700;
      color: var(--breadcrumb-tag-color);
      background-color: ${({ $isCandyBar }) => ($isCandyBar ? 'var(--pivot-bar-tag-bg)' : 'var(--breadcrumb-tag-bg)')};
      /* outline rather than border: a 1px transparent border would grow the pill by 2px in each
         dimension for the legacy default, whereas an inset outline never affects layout. Point's
         own secondary button uses the same trick. */
      outline: 1px solid var(--breadcrumb-tag-border-color);
      outline-offset: -1px;
      border-radius: var(--breadcrumb-tag-radius);
      margin-right: 20px;
    `}

  ${CrumbContent} {
    /* Each fallback is the previous value, so with no theme defining these the rendering is
       unchanged. --darkMode-white is near-white and disappears once the bar is a light surface. */
    color: ${({ $isCandyBar, $border }) =>
      // eslint-disable-next-line no-nested-ternary
      $isCandyBar
        ? $border
          ? 'var(--pivot-bar-tag-color, var(--pivot-bar-bg))'
          : 'var(--pivot-bar-crumb-color, var(--mainBgColor))'
        : 'var(--breadcrumb-crumb-color, var(--darkMode-white))'};
    a:hover {
      color: ${({ $isCandyBar }) =>
        $isCandyBar ? 'var(--pivot-bar-link-hover-color, var(--indigo))' : 'var(--nav-item-selection-indicator)'};
    }
  }

  &:last-child {
    ${CrumbContent} {
      max-width: 500px;
      /* The current page reads as the strongest item in the trail. */
      color: ${({ $isCandyBar }) => !$isCandyBar && 'var(--breadcrumb-crumb-current-color, inherit)'};
      font-weight: var(--breadcrumb-crumb-current-weight, inherit);
    }
  }
`;

export const Separator = styled(ChevronRight)`
  color: var(--breadcrumb-separator-color, var(--nav-item-selection-indicator));
  margin: 1px 7px 1px;
`;

export const CrumbsWrapper = styled.div`
  display: flex;
  padding-left: 0;
  align-items: center;
  width: 100%;
  height: var(--bread-crumb-height);
  a {
    color: inherit;
  }
`;

export const PivotsBarWrapper = styled(CrumbsWrapper)`
  padding-left: ${contentPadding};
  background-color: var(--pivot-bar-bg);
  border-bottom-left-radius: ${!isSingleModeUi() && 'var(--pivot-bar-radius, 30px)'};
  box-shadow: var(--pivot-bar-shadow, 0 0 10px 0 var(--black-300));
  backdrop-filter: blur(5px);

  ${Separator} {
    color: var(--pivot-bar-icon-color, var(--pivot-list-border));
  }
`;

const pivotItemsGap = 20;
const pivotPipeHeight = pivotItemsGap + 15;
export const PivotsList = styled.div`
  padding: 20px 25px;
  display: flex;
  flex-direction: column;
  gap: ${`${pivotItemsGap}px`};
  border-radius: var(--pivot-list-radius, 20px);
  border: 1px solid var(--pivot-list-border);
  background: var(--pivot-list-bg);
  box-shadow: var(--pivot-list-shadow, 0 5px 20px 0 var(--black-300));
  backdrop-filter: blur(15px);
  ${CrumbContent} {
    color: var(--pivot-list-color);
    font-weight: 400;
    a:hover {
      color: var(--pivot-list-hover-color, var(--white));
    }
  }
  ${CrumbWrapper} {
    position: relative;
    :before {
      position: absolute;
      top: 8px;
      left: -10px;
      content: '';
      display: inline-block;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background-color: var(--pivot-bar-color);
    }
    :not(:first-child):after {
      content: '';
      display: inline-block;
      position: absolute;
      bottom: 10px;
      left: -8px;
      height: ${`${pivotPipeHeight}px`};
      border-left: 1px solid var(--pivot-bar-color);
    }
  }
`;

export const PivotItems = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

export const PivotListIcon = styled.div`
  cursor: pointer;
`;

export const PivotsMenu = styled(Menu)`
  .MuiMenu-paper {
    color: unset;
    background-color: unset;
    overflow: unset;
    box-shadow: unset;
  }
`;

export const ClosePivotsBar = styled.div`
  width: 24px;
  cursor: pointer;
  color: var(--pivot-bar-icon-color, var(--pivot-list-border));
  display: flex;
  .dsc-icon-wrapper {
    svg {
      color: var(--pivot-bar-icon-color, var(--pivot-list-border));
    }
  }
`;

export const RightCrumb = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;
